import type {
  OrganizationFixture,
  OrganizationId,
  OrganizationWorkspaceState,
  SystemOperatorAccount,
} from "../types";
import { AUGI_APP_URL, ROOKMERE_APP_URL } from "../workspaceUrls";

export const SYSTEM_OPERATOR_ACCOUNTS: readonly SystemOperatorAccount[] = [
  {
    operatorId: "op_hr_01",
    name: "Soren Lind",
    title: "Platform Operator · Heavy Rain Console",
    scopeLabel: "Cairn & Relay orchestration · Multi-organization review",
  },
  {
    operatorId: "op_hr_02",
    name: "Maren Voss",
    title: "Release & Policy Reviewer · Heavy Rain Console",
    scopeLabel: "Sandbox verification & release promotion",
  },
];

const DEMO_FIXTURES: Record<"rookmere" | "augi", OrganizationFixture> = {
  rookmere: {
    orgId: "rookmere",
    name: "Rookmere Capital",
    shortLabel: "Rookmere Capital",
    legalEntity: "Rookmere Capital Partners LLP",
    sector: "Lower Mid-Market Industrial & B2B Technology Private Equity",
    headquarters: ["London, United Kingdom", "Frankfurt am Main, Germany"],
    totalStaffCount: 32,
    namedRosterCount: 12,
    weeklyBatchId: "batch_rm_2026_w43",
    weeklyCutoffLabel: "2026-10-25 · Cutoff Sun 18:00 UTC",
    capturedThroughLabel: "Captured through 2026-10-25 18:00 UTC",
    totalReportsIngested: 14,
    heroTitle: "Portfolio variance",
    heroFullName: "Portfolio variance report",
    heroOrchestratorEyebrow: "Cairn",
    heroDecisionSummary:
      "6 employee reports across Investment Team and Fund Finance normalized monthly ERP trial balances (SAP, Monitor, Exact, Visma) into Rookmere's standard EUR bridge and checked net leverage covenants. Closest catalog app (Monthly Portfolio KPI Collector v1.1.4) stores 6 manual headline numbers and lacks account mapping, constant-currency FX decomposition, and covenant threshold alerts.",
    heroFailedHeadline:
      "Sandbox verification failed on currency column mapping",
    heroFailedDetail:
      "Portfolio sample row 4 uses GBP without an EUR conversion column in the draft schema. Checkpoint 1 remains saved.",
    externalAppUrl: ROOKMERE_APP_URL,
    checkpoints: [
      {
        step: 1,
        shortLabel: "1. Schema check",
        title: "Stage 1/3: Schema check",
        detail:
          "Verified 12 P&L bridge columns, SEK/NOK/GBP FX conversion rules, and maintenance covenant thresholds against read-only sample headers.",
        toolVerificationOutput:
          "Schema check passed: 12 columns matched across RIG-III (EUR 480m) and RIG-IV (EUR 720m) sample files.",
        durationMs: 1420,
      },
      {
        step: 2,
        shortLabel: "2. UI assembly",
        title: "Stage 2/3: UI assembly",
        detail:
          "Assembling Rookmere Portfolio Variance Workbench using shared organization-ui baseline (rookmere theme): fund filter rail, EBITDA variance waterfall, and 8-company covenant headroom table.",
        toolVerificationOutput:
          "UI assembly compiled: Rookmere theme tokens bound; 0 cross-library imports; interactive FX and cost scenario controls mounted.",
        durationMs: 2890,
      },
      {
        step: 3,
        shortLabel: "3. Verification",
        title: "Stage 3/3: Verification",
        detail:
          "Running 3 automated sandbox checks on read-only sample data: static typecheck, SEK/NOK constant-currency bridge math, and 0 production writes.",
        toolVerificationOutput:
          "3 of 3 verification checks passed on read-only sample data. 0 production writes executed.",
        durationMs: 1640,
      },
    ],
    workers: [
      {
        id: "Researcher",
        stepNumber: 1,
        role: "Researcher",
        headline: "Grouped 14 weekly reports into 3 recurring task clusters",
        runtimeLabel: "Relay intake & catalog matcher",
        assignedScope: "Approved report summaries & catalog metadata",
        latestToolCall: "catalog_match_evaluate(batch='2026-W43', reports=14)",
        latestToolOutput:
          "3 clusters formed: 1 authorized for sandbox build (Portfolio variance report), 1 exact catalog match (Diligence request tracker v2.4), 1 missing Legal sample schedule (LP side-letter obligations).",
        decisionSummary:
          "Matched Diligence request tracker to existing approved catalog app v2.4 without new scope; flagged Portfolio variance gap in Monthly KPI Collector v1.1.4.",
        completedAt: "18:01:12 UTC",
      },
      {
        id: "Connector",
        stepNumber: 2,
        role: "Connector",
        headline: "Locked read-only finance template & FX bridge contract",
        runtimeLabel: "Scoped read-only fixture connector",
        assignedScope: "Approved finance templates (portfolio_financials)",
        latestToolCall:
          "verify_sample_manifest(files=['kelvion_2026_09_tb_headers.csv', 'varn_nordic_fx_bridge_spec.json', 'ic_monthly_variance_table_columns.json'])",
        latestToolOutput:
          "Verified 3 SHA-256 sample attachments from Lukas Weber, Nina Kovacs, and Gareth Jenkins. Host chat transcripts confirmed absent.",
        decisionSummary:
          "Bound read-only sample schema for Fund III (EUR 480m) and Fund IV (EUR 720m) with zero production write credentials.",
        completedAt: "18:02:04 UTC",
      },
      {
        id: "Builder",
        stepNumber: 3,
        role: "Builder",
        headline: "Building Portfolio variance report in isolated sandbox",
        runtimeLabel: "Antigravity sandbox builder · Gemini 4 Argon",
        assignedScope: "packages/organization-ui (theme: rookmere)",
        latestToolCall:
          "sandbox_build_verify(target='portfolio-variance-workbench', checks=['schema', 'ui_assembly', 'fx_covenant_math'])",
        latestToolOutput:
          "Checkpoints 1–3 instrumented: interactive fund filters, FX bps shock slider, EBITDA bridge waterfall, and 8-company covenant table.",
        decisionSummary:
          "Constructing bespoke Rookmere variance workbench against 4 detailed and 4 summary portfolio companies.",
      },
      {
        id: "Notifier",
        stepNumber: 4,
        role: "Notifier",
        headline: "Access-checked routing & release notices queued",
        runtimeLabel: "Relay role-verified notification service",
        assignedScope: "Reporting employees within permitted role_ids",
        latestToolCall:
          "prepare_routing_notices(recipients=['emp_rm_02','emp_rm_03','emp_rm_05','emp_rm_07','emp_rm_08','emp_rm_12'])",
        latestToolOutput:
          "6 release recipients verified for portfolio_financials scope; 5 Diligence tracker routing links ready; 1 Legal evidence request queued for Evelyn Shaw.",
        decisionSummary:
          "Holds Portfolio variance release notice until operator clicks Approve for release; delivers catalog reuse links immediately.",
      },
    ],
    clusters: [
      {
        clusterId: "pat_rm_01",
        title: "Portfolio variance report",
        shortTitle: "Portfolio variance report",
        disposition: "build_in_sandbox",
        actionState: "building_in_sandbox",
        reportCount: 6,
        distinctEmployeeCount: 5,
        departmentsLabel: "Investment Team, Operating Partners & Fund Finance",
        assistantSurfaces: [
          "Claude Desktop",
          "ChatGPT Enterprise",
          "Gemini Workspace",
        ],
        problemSummary:
          "Between business days 5 and 10 each month, deal leads, operating partners, and fund controllers manually map SAP, Monitor, Exact, and Visma trial balances into Rookmere's standard EUR bridge, isolate SEK/NOK/GBP FX translation vs operational variance, and check net leverage covenant headroom across 8 portfolio companies.",
        policyScope: "Approved finance templates · Read-only sample scope",
        matchedAppId: "app_rm_kpi_ingest",
        matchedAppName: "Monthly Portfolio KPI Collector",
        matchedAppVersion: "1.1.4",
        gapExplanation:
          "Monthly Portfolio KPI Collector v1.1.4 stores only 6 manual headline numbers and lacks ERP trial-balance mapping, constant-currency FX decomposition, and maintenance covenant alerts.",
        recommendedAction:
          "Commission sandbox build of Portfolio variance report using shared organization-ui Rookmere theme.",
        reportIds: [
          "rep_rm_20261021_001",
          "rep_rm_20261022_002",
          "rep_rm_20261022_003",
        ],
      },
      {
        clusterId: "pat_rm_02",
        title: "Diligence request tracker",
        shortTitle: "Diligence request tracker",
        disposition: "reuse_existing_app",
        actionState: "pending_triage",
        reportCount: 5,
        distinctEmployeeCount: 3,
        departmentsLabel: "Investment Team & Compliance",
        assistantSurfaces: ["Claude Desktop", "ChatGPT Enterprise"],
        problemSummary:
          "Associates on new RIG-IV industrial buyout screens pasted advisor IRL spreadsheets into chat to merge duplicate commercial, financial, and legal diligence questions and assign seller management owners.",
        policyScope: "Deal confidential · Existing role_deal_team permission",
        matchedAppId: "app_rm_diligence_tracker",
        matchedAppName: "Diligence Request Tracker",
        matchedAppVersion: "2.4",
        gapExplanation:
          "Matches approved existing app Diligence Request Tracker v2.4. Reporting employees already hold permitted role access; no new data scope required.",
        recommendedAction:
          "Route directly to existing approved app without adding a redundant approval gate.",
        reportIds: ["rep_rm_20261023_004"],
      },
      {
        clusterId: "pat_rm_03",
        title: "LP side-letter obligations",
        shortTitle: "LP side-letter obligations",
        disposition: "request_missing_evidence",
        actionState: "pending_triage",
        reportCount: 3,
        distinctEmployeeCount: 3,
        departmentsLabel: "Compliance & Investor Relations",
        assistantSurfaces: ["Claude Desktop", "Gemini Workspace"],
        problemSummary:
          "Investor Relations and Compliance staff described recurring checks of RIG-III and RIG-IV LP side-letter clauses (MFN fee breaks, 5-day co-invest notice windows, ESG exclusions), but withheld sample clause structures under lp_restricted_side_letters policy.",
        policyScope:
          "LP summary commitments · Needs Legal redacted sample schedule",
        gapExplanation:
          "3 Compliance and IR reports describe this task, but no approved redacted sample obligation schedule is attached from Legal.",
        recommendedAction:
          "Request 1 redacted sample obligation schedule from Evelyn Shaw (General Counsel & CCO) while other weekly tasks continue unblocked.",
        missingEvidenceDetail:
          "Needs 1 redacted sample obligation schedule (.json or .xlsx) and lp_restricted_side_letters schema sign-off from Legal.",
        missingEvidenceAssignee: "Evelyn Shaw (General Counsel & CCO)",
        reportIds: ["rep_rm_20261023_005"],
      },
    ],
    reports: [
      {
        reportId: "rep_rm_20261021_001",
        receiptId: "rcpt_rm_8841a",
        submittedAt: "2026-10-21 16:14 UTC",
        employeeId: "emp_rm_02",
        employeeName: "Lukas Weber",
        employeeTitle: "Principal, DACH Investments",
        departmentLabel: "Investment Team",
        office: "Frankfurt",
        assistantSurface: "Claude Desktop",
        mcpToolName: "heavyrain_submit_work_report",
        userInspectedOutgoingPayload: true,
        hostChatHistoryExcluded: true,
        clusterId: "pat_rm_01",
        dataScope: "portfolio_financials",
        policyScopeLabel: "Approved finance templates",
        taskSummary:
          "Normalized September HGB trial balance CSV for Kelvion Flow Systems GmbH against the RIG-III board budget and isolated the EUR 430k valve-skid commissioning revenue timing variance.",
        outgoingPayloadPreview: {
          receipt_id: "rcpt_rm_8841a",
          organization: "Rookmere Capital",
          employee: "Lukas Weber (Principal, DACH Investments)",
          recurring_cadence: "Monthly (Business days 5–8)",
          portfolio_scope: ["Kelvion Flow Systems GmbH (RIG-III)"],
          approved_scope: "portfolio_financials",
          chat_transcript_included: false,
          attached_files: [
            "kelvion_2026_09_tb_headers.csv",
            "rig_iii_standard_pnl_mapping.json",
          ],
        },
        attachments: [
          {
            filename: "kelvion_2026_09_tb_headers.csv",
            kind: "Schema & anonymized sample rows",
            bytes: 4120,
            sha256: "8f2a9c41d0e8b712...a5b6c7d8",
            policyStatus: "approved",
          },
          {
            filename: "rig_iii_standard_pnl_mapping.json",
            kind: "P&L account mapping rules",
            bytes: 6840,
            sha256: "2c91b4e7f1a09833...6d7e8f90",
            policyStatus: "approved",
          },
        ],
      },
      {
        reportId: "rep_rm_20261022_002",
        receiptId: "rcpt_rm_8849c",
        submittedAt: "2026-10-22 11:05 UTC",
        employeeId: "emp_rm_08",
        employeeName: "Nina Kovacs",
        employeeTitle: "Senior Fund Controller",
        departmentLabel: "Fund Finance & Valuation",
        office: "Frankfurt",
        assistantSurface: "ChatGPT Enterprise",
        mcpToolName: "heavyrain_submit_work_report",
        userInspectedOutgoingPayload: true,
        hostChatHistoryExcluded: true,
        clusterId: "pat_rm_01",
        dataScope: "portfolio_financials",
        policyScopeLabel: "Approved finance templates",
        taskSummary:
          "Converted Varn Precision Optics (SEK) and Nordic ColdChain (NOK) September management packs into EUR at budget vs actual FX rates and flagged Nordic ColdChain's 3.60x net leverage against its 3.75x maintenance covenant.",
        outgoingPayloadPreview: {
          receipt_id: "rcpt_rm_8849c",
          organization: "Rookmere Capital",
          employee: "Nina Kovacs (Senior Fund Controller)",
          recurring_cadence: "Monthly (Business days 6–10)",
          portfolio_scope: [
            "Varn Precision Optics AB (SEK · RIG-III)",
            "Nordic ColdChain Logistics AS (NOK · RIG-IV)",
          ],
          approved_scope: "portfolio_financials",
          chat_transcript_included: false,
          attached_files: ["varn_nordic_fx_bridge_spec.json"],
        },
        attachments: [
          {
            filename: "varn_nordic_fx_bridge_spec.json",
            kind: "FX & covenant calculation specification",
            bytes: 5390,
            sha256: "4d18e2a9c7b65012...bcdef012",
            policyStatus: "approved",
          },
        ],
      },
      {
        reportId: "rep_rm_20261022_003",
        receiptId: "rcpt_rm_8854d",
        submittedAt: "2026-10-22 14:48 UTC",
        employeeId: "emp_rm_07",
        employeeName: "Gareth Jenkins",
        employeeTitle: "Head of Fund Finance & Valuation",
        departmentLabel: "Fund Finance & Valuation",
        office: "London",
        assistantSurface: "Gemini Workspace",
        mcpToolName: "heavyrain_submit_work_report",
        userInspectedOutgoingPayload: true,
        hostChatHistoryExcluded: true,
        clusterId: "pat_rm_01",
        dataScope: "portfolio_financials",
        policyScopeLabel: "Approved finance templates",
        taskSummary:
          "Consolidated all 8 portfolio company September EBITDA and net-debt variances across RIG-III (EUR 480m) and RIG-IV (EUR 720m) into the Monday Partner Investment Committee deck table.",
        outgoingPayloadPreview: {
          receipt_id: "rcpt_rm_8854d",
          organization: "Rookmere Capital",
          employee: "Gareth Jenkins (Head of Fund Finance & Valuation)",
          recurring_cadence: "Monthly IC pre-read",
          portfolio_scope: ["All 8 active portfolio companies (RIG-III & RIG-IV)"],
          approved_scope: "portfolio_financials",
          sandbox_build_authorized: true,
          chat_transcript_included: false,
          attached_files: ["ic_monthly_variance_table_columns.json"],
        },
        attachments: [
          {
            filename: "ic_monthly_variance_table_columns.json",
            kind: "Output table schema contract",
            bytes: 3180,
            sha256: "7a8b9c0d1e2f3a4b...5e6f7a8b",
            policyStatus: "approved",
          },
        ],
      },
      {
        reportId: "rep_rm_20261023_004",
        receiptId: "rcpt_rm_8862f",
        submittedAt: "2026-10-23 09:32 UTC",
        employeeId: "emp_rm_04",
        employeeName: "Maya Patel",
        employeeTitle: "Associate, Investment Team",
        departmentLabel: "Investment Team",
        office: "London",
        assistantSurface: "Claude Desktop",
        mcpToolName: "heavyrain_submit_work_report",
        userInspectedOutgoingPayload: true,
        hostChatHistoryExcluded: true,
        clusterId: "pat_rm_02",
        dataScope: "deal_confidential",
        policyScopeLabel: "Deal confidential VDR headers",
        taskSummary:
          "Merged 140 financial and commercial diligence questions from two advisor workstreams into a single deduplicated VDR request list with seller owner assignments.",
        outgoingPayloadPreview: {
          receipt_id: "rcpt_rm_8862f",
          organization: "Rookmere Capital",
          employee: "Maya Patel (Associate, Investment Team)",
          recurring_cadence: "Per buyout diligence sprint",
          approved_scope: "deal_confidential",
          chat_transcript_included: false,
          attached_files: ["irl_column_headers_sample.csv"],
        },
        attachments: [
          {
            filename: "irl_column_headers_sample.csv",
            kind: "Column headers only (.csv)",
            bytes: 1240,
            sha256: "9e0f1a2b3c4d5e6f...7c8d9e0f",
            policyStatus: "approved",
          },
        ],
      },
      {
        reportId: "rep_rm_20261023_005",
        receiptId: "rcpt_rm_8870b",
        submittedAt: "2026-10-23 17:19 UTC",
        employeeId: "emp_rm_09",
        employeeName: "Sophie Albers",
        employeeTitle: "Director of Investor Relations",
        departmentLabel: "Investor Relations",
        office: "London",
        assistantSurface: "Claude Desktop",
        mcpToolName: "heavyrain_submit_work_report",
        userInspectedOutgoingPayload: true,
        hostChatHistoryExcluded: true,
        clusterId: "pat_rm_03",
        dataScope: "lp_summary_commitments",
        policyScopeLabel: "LP summary commitments (Side letters withheld)",
        taskSummary:
          "Checked which RIG-IV LPs hold co-investment right-of-first-offer notice windows under 5 business days before allocating the Solvay Grid Controls follow-on tranche.",
        outgoingPayloadPreview: {
          receipt_id: "rcpt_rm_8870b",
          organization: "Rookmere Capital",
          employee: "Sophie Albers (Director of Investor Relations)",
          recurring_cadence: "Per co-investment & quarterly fee check",
          approved_scope: "lp_summary_commitments",
          chat_transcript_included: false,
          attached_files: [],
          withheld_scope_reason:
            "lp_restricted_side_letters requires General Counsel redacted sample schedule",
        },
        attachments: [],
        missingEvidenceNote:
          "Side-letter clause text and obligation taxonomy omitted at submission time because lp_restricted_side_letters requires General Counsel redaction and schema approval.",
      },
    ],
    catalogApps: [
      {
        appId: "app_rm_variance_workbench",
        name: "Portfolio variance report",
        shortName: "Portfolio variance report",
        slug: "portfolio-variance",
        url: ROOKMERE_APP_URL,
        version: "1.0.0-rc1",
        releaseStatus: "in_sandbox_review",
        ownerName: "Gareth Jenkins",
        ownerTitle: "Head of Fund Finance & Valuation",
        permittedRoles: [
          "Fund Finance & Valuation",
          "Investment Team",
          "Operating Partners",
        ],
        dataScopes: ["portfolio_financials (Read-only ERP trial balances)"],
        summary:
          "Normalizes monthly portfolio trial balances across RIG-III (EUR 480m) and RIG-IV (EUR 720m), decomposes constant-currency vs FX variance (SEK, NOK, GBP), and monitors maintenance covenant headroom.",
        capabilities: [
          "Filters across RIG-III (EUR 480m) and RIG-IV (EUR 720m) portfolio companies with Reported EUR vs Constant Budget FX toggle.",
          "Runs interactive FX bps and input-cost scenario adjustments with live EBITDA waterfall bridge and covenant headroom alerts.",
        ],
        nonCapabilities: [
          "Read-only sandbox/fixture analytical view; does not write journal entries back to SAP, Monitor, Exact, or Visma ERPs.",
        ],
        notifiedEmployeeCount: 0,
        isHeroCandidate: true,
        feedbackEntries: [
          {
            id: "fb_rm_01",
            employeeName: "Nina Kovacs",
            role: "Senior Fund Controller",
            submittedAt: "2026-10-25 17:40 UTC",
            comment:
              "Confirm Nordic ColdChain 3.60x leverage row highlights when covenant alert buffer is set to 0.20x.",
            routedToWork: false,
          },
        ],
      },
      {
        appId: "app_rm_diligence_tracker",
        name: "Diligence Request List & Q&A Tracker",
        shortName: "Diligence request tracker",
        slug: "diligence-tracker",
        url: "https://apps.rookmere.example/diligence-tracker",
        version: "2.4.1",
        releaseStatus: "catalog_approved",
        ownerName: "Lukas Weber",
        ownerTitle: "Principal, DACH Investments",
        permittedRoles: [
          "Investment Team",
          "Operating Partners",
          "Compliance & Legal",
        ],
        dataScopes: ["deal_confidential"],
        summary:
          "Ingests VDR request spreadsheets (XLSX/CSV), deduplicates overlapping commercial, financial, and legal diligence questions, and tracks response aging.",
        capabilities: [
          "Deduplicates multi-advisor IRL spreadsheets and assigns seller management owners.",
          "Exports weekly buyout diligence status packs within existing deal_confidential scope.",
        ],
        nonCapabilities: [
          "Does not perform post-acquisition monthly ERP trial-balance variance mapping.",
        ],
        notifiedEmployeeCount: 5,
        lastReleasedAt: "2026-09-14 09:00 UTC",
        feedbackEntries: [
          {
            id: "fb_rm_02",
            employeeName: "Maya Patel",
            role: "Associate, Investment Team",
            submittedAt: "2026-10-23 10:15 UTC",
            comment:
              "Pre-mapped CSV import template handled 140 advisor questions cleanly once routed.",
            routedToWork: false,
          },
        ],
      },
      {
        appId: "app_rm_valuation_bridge",
        name: "Quarterly IPEV Valuation Bridge",
        shortName: "IPEV valuation bridge",
        slug: "valuation-bridge",
        url: "https://apps.rookmere.example/valuation-bridge",
        version: "1.9.0",
        releaseStatus: "catalog_approved",
        ownerName: "Gareth Jenkins",
        ownerTitle: "Head of Fund Finance & Valuation",
        permittedRoles: [
          "Fund Finance & Valuation",
          "Investment Team",
          "Investor Relations",
        ],
        dataScopes: ["portfolio_financials"],
        summary:
          "Computes quarterly enterprise-value to equity-value bridges decomposing LTM EBITDA growth, multiple expansion, net debt paydown, and FX.",
        capabilities: [
          "Generates audited LP quarterly report tables for RIG-III and RIG-IV.",
        ],
        nonCapabilities: [
          "Operates on quarterly signed-off LTM totals only; does not ingest monthly ERP trial balances.",
        ],
        notifiedEmployeeCount: 8,
        lastReleasedAt: "2026-08-30 14:20 UTC",
        feedbackEntries: [],
      },
      {
        appId: "app_rm_kpi_ingest",
        name: "Monthly Portfolio KPI Collector",
        shortName: "Monthly KPI collector",
        slug: "kpi-collector",
        url: "https://apps.rookmere.example/kpi-collector",
        version: "1.1.4",
        releaseStatus: "catalog_approved",
        ownerName: "Nina Kovacs",
        ownerTitle: "Senior Fund Controller",
        permittedRoles: [
          "Fund Finance & Valuation",
          "Investment Team",
          "Operating Partners",
        ],
        dataScopes: ["portfolio_financials"],
        summary:
          "Collects 6 headline monthly metrics (Revenue, Gross Margin %, Order Backlog, Headcount, Cash, Gross Debt) entered manually by portfolio CFOs.",
        capabilities: [
          "Standardizes manual CFO headline metric submissions across 8 portfolio companies.",
        ],
        nonCapabilities: [
          "Does not parse ERP account-level trial balances or compute maintenance covenant headroom.",
        ],
        notifiedEmployeeCount: 12,
        lastReleasedAt: "2026-06-10 11:00 UTC",
        feedbackEntries: [],
      },
    ],
    initialEvents: [
      {
        eventId: "evt_rm_01",
        timestamp: "18:00:04 UTC",
        actor: "Relay",
        stageLabel: "Weekly cutoff intake",
        tone: "completed",
        headline:
          "Ingested 14 employee-reviewed work reports at Sunday 18:00 UTC cutoff",
        toolName: "relay_ingest_cutoff_batch",
        inputEvidence:
          "14 signed submission receipts (rcpt_rm_8841a .. rcpt_rm_8870b); 0 chat transcripts",
        toolOutput:
          "Validated attachment extensions (.csv, .json, .xlsx) and SHA-256 digests against Rookmere capture policy.",
        durationMs: 680,
        decisionSummary:
          "Confirmed every outgoing payload was previewed by the submitting employee; forwarded batch_rm_2026_w43 to Cairn.",
      },
      {
        eventId: "evt_rm_02",
        timestamp: "18:01:12 UTC",
        actor: "Researcher",
        stageLabel: "Recurrence & catalog match",
        tone: "completed",
        headline:
          "Grouped 14 reports into 3 recurring task groups and checked approved catalog",
        toolName: "catalog_match_evaluate",
        inputEvidence:
          "batch_rm_2026_w43 + 4 approved Rookmere catalog manifests",
        toolOutput:
          "pat_rm_01 (6 reports): gap in Monthly KPI Collector v1.1.4; pat_rm_02 (5 reports): exact match to Diligence Request Tracker v2.4; pat_rm_03 (3 reports): missing Legal sample file.",
        durationMs: 1190,
        decisionSummary:
          "Recommended direct catalog routing for Diligence request tracker, evidence request for LP side-letter obligations, and sandbox build for Portfolio variance report.",
      },
      {
        eventId: "evt_rm_03",
        timestamp: "18:02:04 UTC",
        actor: "Connector",
        stageLabel: "Checkpoint 1 · Schema check",
        tone: "completed",
        headline:
          "Checkpoint 1 passed: 12 P&L bridge columns and SEK/NOK FX contract verified",
        toolName: "schema_contract_check",
        inputEvidence:
          "kelvion_2026_09_tb_headers.csv, varn_nordic_fx_bridge_spec.json, ic_monthly_variance_table_columns.json",
        toolOutput:
          "Schema check passed: 12 columns matched; read-only portfolio_financials scope verified for Fund III (EUR 480m) and Fund IV (EUR 720m).",
        durationMs: 1420,
        decisionSummary:
          "Saved Checkpoint 1 snapshot so any subsequent pause or retry skips schema re-ingestion.",
        checkpointStep: 1,
      },
      {
        eventId: "evt_rm_04",
        timestamp: "18:03:19 UTC",
        actor: "Cairn",
        stageLabel: "Sandbox orchestration",
        tone: "completed",
        headline:
          "Commissioned isolated sandbox Builder for Portfolio variance report",
        toolName: "cairn_dispatch_sandbox_builder",
        inputEvidence:
          "Decision dec_rm_w43_01 · Authorized by Gareth Jenkins (Head of Fund Finance)",
        toolOutput:
          "Assigned Builder in isolated branch workspace with @heavy-rain/organization-ui (rookmere theme) and read-only sample fixtures.",
        durationMs: 540,
        decisionSummary:
          "Selected bespoke variance workbench architecture with fund filter rail, EBITDA waterfall bridge, and covenant headroom alerts.",
      },
      {
        eventId: "evt_rm_05",
        timestamp: "18:04:08 UTC",
        actor: "Builder",
        stageLabel: "Checkpoint 2 · UI assembly",
        tone: "building",
        headline:
          "Assembling Rookmere Portfolio Variance Workbench components in sandbox",
        toolName: "sandbox_assemble_ui",
        inputEvidence:
          "Checkpoint 1 schema contract + Rookmere limestone/slate theme tokens",
        toolOutput:
          "Stage 2/3 running: mounting Fund III/IV filter controls, FX bps scenario slider, EBITDA bridge chart, and 8-company variance ledger.",
        durationMs: 2890,
        decisionSummary:
          "Preview remains veiled behind smoky blur until Stage 3 verification checks pass.",
        checkpointStep: 2,
      },
    ],
    rookmereCompanies: [
      {
        companyId: "port_kelvion_flow",
        detailLevel: "full",
        name: "Kelvion Flow Systems GmbH",
        fundId: "fund_rm_iii",
        fundShortCode: "RIG-III",
        headquarters: "Mannheim, Germany",
        subsector: "Precision cryogenic & industrial process valves",
        localCurrency: "EUR",
        fxRateBudget: 1.0,
        fxRateActual: 1.0,
        erpSystem: "SAP Business ByDesign",
        submissionFormat: "German HGB trial balance CSV + commentary PDF",
        dealLead: "Lukas Weber",
        operatingLead: "Dr. Stefan Kroll",
        financeReviewer: "Gareth Jenkins",
        latestPeriod: "2026-09",
        revenueActualEur: 6420000,
        revenueBudgetEur: 6850000,
        ebitdaActualEur: 1180000,
        ebitdaBudgetEur: 1410000,
        fxTranslationImpactEur: 0,
        volumeMixImpactEur: -175000,
        costScrapImpactEur: -55000,
        netDebtEur: 41200000,
        ltmEbitdaEur: 13100000,
        netLeverageRatio: 3.15,
        covenantMaxLeverage: 3.75,
        primaryVarianceDriver:
          "Delayed commissioning of two chemical-plant valve skids shifted EUR 430k revenue into October; stainless-steel casting scrap rose 140 bps.",
      },
      {
        companyId: "port_varn_optics",
        detailLevel: "full",
        name: "Varn Precision Optics AB",
        fundId: "fund_rm_iii",
        fundShortCode: "RIG-III",
        headquarters: "Linköping, Sweden",
        subsector: "SWIR lenses for semiconductor & battery inspection",
        localCurrency: "SEK",
        fxRateBudget: 11.2,
        fxRateActual: 11.48,
        erpSystem: "Monitor ERP G5",
        submissionFormat: "Multi-tab XLSX management pack in SEK",
        dealLead: "Henrik Lindqvist",
        operatingLead: "Elise Moreau",
        financeReviewer: "Nina Kovacs",
        latestPeriod: "2026-09",
        revenueActualEur: 4890000,
        revenueBudgetEur: 4650000,
        ebitdaActualEur: 1340000,
        ebitdaBudgetEur: 1210000,
        fxTranslationImpactEur: -33000,
        volumeMixImpactEur: 185000,
        costScrapImpactEur: -22000,
        netDebtEur: 24500000,
        ltmEbitdaEur: 14200000,
        netLeverageRatio: 1.73,
        covenantMaxLeverage: 3.5,
        primaryVarianceDriver:
          "Constant-currency revenue beat budget by +7.8% on OEM inline inspection orders; SEK depreciation reduced EUR-translated EBITDA by EUR 33k.",
      },
      {
        companyId: "port_solvay_grid",
        detailLevel: "full",
        name: "Solvay Grid Controls B.V.",
        fundId: "fund_rm_iv",
        fundShortCode: "RIG-IV",
        headquarters: "Eindhoven, Netherlands",
        subsector: "Medium-voltage substation protection relays & telemetry",
        localCurrency: "EUR",
        fxRateBudget: 1.0,
        fxRateActual: 1.0,
        erpSystem: "Exact Online Manufacturing",
        submissionFormat: "Exact Online GL export CSV + ARR schedule XLSX",
        dealLead: "Clara Vance",
        operatingLead: "Dr. Stefan Kroll",
        financeReviewer: "Gareth Jenkins",
        latestPeriod: "2026-09",
        revenueActualEur: 7910000,
        revenueBudgetEur: 7600000,
        ebitdaActualEur: 1920000,
        ebitdaBudgetEur: 1850000,
        fxTranslationImpactEur: 0,
        volumeMixImpactEur: 135000,
        costScrapImpactEur: -65000,
        netDebtEur: 58000000,
        ltmEbitdaEur: 21400000,
        netLeverageRatio: 2.71,
        covenantMaxLeverage: 4.0,
        primaryVarianceDriver:
          "Municipal DSO grid retrofit deliveries pulled forward; firmware engineering contractor spend ran EUR 65k above monthly budget.",
      },
      {
        companyId: "port_nordic_cold",
        detailLevel: "full",
        name: "Nordic ColdChain Logistics AS",
        fundId: "fund_rm_iv",
        fundShortCode: "RIG-IV",
        headquarters: "Oslo, Norway",
        subsector: "GDP temperature-controlled pharma freight & depot storage",
        localCurrency: "NOK",
        fxRateBudget: 11.5,
        fxRateActual: 11.79,
        erpSystem: "Visma.net ERP",
        submissionFormat: "Visma trial balance + fuel surcharge XLSX",
        dealLead: "Maya Patel",
        operatingLead: "Elise Moreau",
        financeReviewer: "Nina Kovacs",
        latestPeriod: "2026-09",
        revenueActualEur: 5580000,
        revenueBudgetEur: 5920000,
        ebitdaActualEur: 890000,
        ebitdaBudgetEur: 1140000,
        fxTranslationImpactEur: -28000,
        volumeMixImpactEur: -182000,
        costScrapImpactEur: -40000,
        netDebtEur: 39800000,
        ltmEbitdaEur: 11050000,
        netLeverageRatio: 3.6,
        covenantMaxLeverage: 3.75,
        primaryVarianceDriver:
          "Two biologicals distribution contracts paused lane expansion in Q3; net leverage (3.60x) is within 0.15x of the 3.75x maintenance covenant threshold.",
      },
      {
        companyId: "port_brenthal_pack",
        detailLevel: "summary",
        name: "Brenthal Protective Packaging Ltd",
        fundId: "fund_rm_iii",
        fundShortCode: "RIG-III",
        headquarters: "Manchester, United Kingdom",
        subsector: "Custom moulded-fibre industrial transit packaging",
        localCurrency: "GBP",
        fxRateBudget: 0.85,
        fxRateActual: 0.84,
        erpSystem: "Dynamics 365 Business Central",
        submissionFormat: "Summary KPI pack (GBP translated to EUR)",
        dealLead: "Clara Vance",
        operatingLead: "Dr. Stefan Kroll",
        financeReviewer: "Gareth Jenkins",
        latestPeriod: "2026-09",
        revenueActualEur: 4120000,
        revenueBudgetEur: 4050000,
        ebitdaActualEur: 940000,
        ebitdaBudgetEur: 920000,
        fxTranslationImpactEur: 11000,
        volumeMixImpactEur: 19000,
        costScrapImpactEur: -10000,
        netDebtEur: 19400000,
        ltmEbitdaEur: 9800000,
        netLeverageRatio: 1.98,
        covenantMaxLeverage: 3.5,
        primaryVarianceDriver:
          "On plan (+2.1% YTD EBITDA vs budget); GBP appreciation added EUR 11k on translation.",
      },
      {
        companyId: "port_ostmark_med",
        detailLevel: "summary",
        name: "Ostmark Sterile Components GmbH",
        fundId: "fund_rm_iii",
        fundShortCode: "RIG-III",
        headquarters: "Graz, Austria",
        subsector: "Cleanroom elastomer stoppers & plungers for vials",
        localCurrency: "EUR",
        fxRateBudget: 1.0,
        fxRateActual: 1.0,
        erpSystem: "proALPHA ERP",
        submissionFormat: "Summary monthly pack",
        dealLead: "Lukas Weber",
        operatingLead: "Dr. Stefan Kroll",
        financeReviewer: "Gareth Jenkins",
        latestPeriod: "2026-09",
        revenueActualEur: 5190000,
        revenueBudgetEur: 5050000,
        ebitdaActualEur: 1290000,
        ebitdaBudgetEur: 1240000,
        fxTranslationImpactEur: 0,
        volumeMixImpactEur: 62000,
        costScrapImpactEur: -12000,
        netDebtEur: 28100000,
        ltmEbitdaEur: 13600000,
        netLeverageRatio: 2.07,
        covenantMaxLeverage: 3.75,
        primaryVarianceDriver:
          "Slightly ahead of plan; cleanroom Line 4 qualification completed in September.",
      },
      {
        companyId: "port_therma_rail",
        detailLevel: "summary",
        name: "ThermaRail HVAC SAS",
        fundId: "fund_rm_iv",
        fundShortCode: "RIG-IV",
        headquarters: "Lyon, France",
        subsector: "Low-GWP refrigerant HVAC modules for regional rail",
        localCurrency: "EUR",
        fxRateBudget: 1.0,
        fxRateActual: 1.0,
        erpSystem: "Sage X3",
        submissionFormat: "Summary monthly pack",
        dealLead: "Henrik Lindqvist",
        operatingLead: "Elise Moreau",
        financeReviewer: "Nina Kovacs",
        latestPeriod: "2026-09",
        revenueActualEur: 6080000,
        revenueBudgetEur: 6300000,
        ebitdaActualEur: 1020000,
        ebitdaBudgetEur: 1110000,
        fxTranslationImpactEur: 0,
        volumeMixImpactEur: -58000,
        costScrapImpactEur: -32000,
        netDebtEur: 36400000,
        ltmEbitdaEur: 11200000,
        netLeverageRatio: 3.25,
        covenantMaxLeverage: 3.75,
        primaryVarianceDriver:
          "Watchlist on working capital; compressor supplier lead times extended by 3 weeks.",
      },
      {
        companyId: "port_castel_lab",
        detailLevel: "summary",
        name: "Castel Analytical Instruments S.r.l.",
        fundId: "fund_rm_iv",
        fundShortCode: "RIG-IV",
        headquarters: "Modena, Italy",
        subsector: "Automated chromatography sample handlers",
        localCurrency: "EUR",
        fxRateBudget: 1.0,
        fxRateActual: 1.0,
        erpSystem: "TeamSystem Enterprise",
        submissionFormat: "First 100-day integration pack",
        dealLead: "Maya Patel",
        operatingLead: "Elise Moreau",
        financeReviewer: "Nina Kovacs",
        latestPeriod: "2026-09",
        revenueActualEur: 3840000,
        revenueBudgetEur: 3800000,
        ebitdaActualEur: 860000,
        ebitdaBudgetEur: 850000,
        fxTranslationImpactEur: 0,
        volumeMixImpactEur: 18000,
        costScrapImpactEur: -8000,
        netDebtEur: 22900000,
        ltmEbitdaEur: 9400000,
        netLeverageRatio: 2.44,
        covenantMaxLeverage: 3.75,
        primaryVarianceDriver:
          "First 100-day integration on track; chart-of-accounts mapping to Rookmere standard in progress.",
      },
    ],
  },
  augi: {
    orgId: "augi",
    name: "AUGI",
    shortLabel: "AUGI",
    legalEntity: "AUGI Mobility Engineering GmbH",
    sector: "Electric Vehicle Platform & Body-in-White Engineering",
    headquarters: ["Munich, Germany", "Gothenburg, Sweden"],
    totalStaffCount: 48,
    namedRosterCount: 8,
    weeklyBatchId: "batch_augi_2026_w43",
    weeklyCutoffLabel: "2026-10-25 · Cutoff Sun 18:00 UTC",
    capturedThroughLabel: "Captured through 2026-10-25 18:00 UTC",
    totalReportsIngested: 16,
    heroTitle: "AUGI Sable chassis study",
    heroFullName: "AUGI Sable chassis study",
    heroOrchestratorEyebrow: "Cairn",
    heroDecisionSummary:
      "7 Body-in-White, Battery Pack, and Vehicle Dynamics engineers reported repeated ad-hoc scripts comparing SABLE-A (extruded frame), SABLE-B (cell-to-body boron hybrid), and SABLE-C (front/rear megacastings) across wheelbase, track, and wall gauge. Closest catalog app (Sable Mass Ledger v3.1.0) is a static 2D table with no interactive 3D skateboard geometry or torsion/bending load-case approximations.",
    heroFailedHeadline:
      "Sandbox verification failed on wheelbase bounds check",
    heroFailedDetail:
      "Sample parameter sweep row 6 specifies 3340 mm wheelbase outside the 2920–3160 mm Gate C2 platform envelope. Checkpoint 1 remains saved.",
    externalAppUrl: AUGI_APP_URL,
    augiInlineDisclosure:
      "Concept beam-and-shell approximation (units: mm, kg, kN·m/deg). Analytical scaling for architecture comparison only—not a finite-element solver, physical rig test, or crashworthiness certification.",
    checkpoints: [
      {
        step: 1,
        shortLabel: "1. Schema check",
        title: "Stage 1/3: Schema check",
        detail:
          "Verified Gate C2 parametric envelope bounds (Wheelbase 2920–3160 mm, Gauge 2.2–4.5 mm), material cards (6082-T6, AlSi7Mg C611, 22MnB5 Boron), and analytical scaling equations.",
        toolVerificationOutput:
          "Schema check passed: 3 baseline configurations (SABLE-A, SABLE-B, SABLE-C) and 3 load cases validated.",
        durationMs: 1310,
      },
      {
        step: 2,
        shortLabel: "2. UI assembly",
        title: "Stage 2/3: UI assembly",
        detail:
          "Assembling AUGI Sable 3D Chassis Comparator (apps/augi-engineering on port 43171) using packages/organization-ui (augi theme) and interactive Three.js / React Three Fiber skateboard geometry.",
        toolVerificationOutput:
          "3D scene & parameter dock compiled: orbit/pan/zoom camera presets, Baseline vs Candidate comparison, and inline concept-estimate disclosures mounted.",
        durationMs: 3180,
      },
      {
        step: 3,
        shortLabel: "3. Verification",
        title: "Stage 3/3: Verification",
        detail:
          "Running 3 automated sandbox checks: Bredt-Batho torsion & Euler-Bernoulli bending unit checks, reduced-motion static step pose fallback, and 0 production writes.",
        toolVerificationOutput:
          "3 of 3 verification checks passed on read-only concept parameters. 0 production writes executed.",
        durationMs: 1590,
      },
    ],
    workers: [
      {
        id: "Researcher",
        stepNumber: 1,
        role: "Researcher",
        headline: "Grouped 16 engineering reports into 3 Gate C2 clusters",
        runtimeLabel: "Relay intake & catalog matcher",
        assignedScope: "Approved parametric envelopes & catalog metadata",
        latestToolCall: "catalog_match_evaluate(batch='2026-W43-AUGI', reports=16)",
        latestToolOutput:
          "3 clusters formed: 1 authorized for 3D sandbox build (AUGI Sable chassis study), 1 exact catalog match (Supplier material & joining lookup v2.0.3), 1 awaiting foundry NDA clearance.",
        decisionSummary:
          "Matched joining lookup to BIW Mixed-Material Joining Registry v2.0.3; identified 3D parametric geometry gap in static Sable Mass Ledger v3.1.0.",
        completedAt: "18:01:08 UTC",
      },
      {
        id: "Connector",
        stepNumber: 2,
        role: "Connector",
        headline: "Verified parametric envelope & CAD IP boundary",
        runtimeLabel: "Scoped read-only fixture connector",
        assignedScope: "Concept geometry parameters (sable_parametric_geometry)",
        latestToolCall:
          "verify_sample_manifest(files=['sable_gate_c2_envelopes.json', 'bredt_batho_scaling_spec.csv'])",
        latestToolOutput:
          "Confirmed native CATIA/NX solid assemblies remained on workstation; only parametric envelopes and material card IDs ingested.",
        decisionSummary:
          "Bound read-only SABLE-A, SABLE-B, and SABLE-C baseline tables for sandbox 3D builder.",
        completedAt: "18:01:55 UTC",
      },
      {
        id: "Builder",
        stepNumber: 3,
        role: "Builder",
        headline: "Building AUGI Sable 3D chassis study in sandbox",
        runtimeLabel: "Antigravity sandbox builder · Gemini 4 Argon",
        assignedScope: "apps/augi-engineering · packages/organization-ui (augi)",
        latestToolCall:
          "sandbox_build_verify(target='augi-sable-chassis-3d', port=43171, checks=['geometry_bounds', 'torsion_bending_units', 'inline_disclosures'])",
        latestToolOutput:
          "Interactive 3D skateboard chassis, load-case deformation shader, and side-by-side SABLE-A/B/C comparator verified.",
        decisionSummary:
          "Embedding live AUGI engineering workbench (http://127.0.0.1:43171) with explicit concept-approximation disclosures beside every readout.",
      },
      {
        id: "Notifier",
        stepNumber: 4,
        role: "Notifier",
        headline: "Engineering release & catalog routing links queued",
        runtimeLabel: "Relay role-verified notification service",
        assignedScope: "BIW, Battery, Casting & Vehicle Dynamics roles",
        latestToolCall:
          "prepare_routing_notices(recipients=['emp_augi_01','emp_augi_02','emp_augi_03','emp_augi_04','emp_augi_05','emp_augi_07','emp_augi_08'])",
        latestToolOutput:
          "7 structures engineers queued for AUGI Sable 3D comparator release link; 4 engineers routed to BIW Joining Registry v2.0.3.",
        decisionSummary:
          "Awaits Devendra Rao / Operator release approval before dispatching AUGI Sable 3D comparator links.",
      },
    ],
    clusters: [
      {
        clusterId: "wf_augi_01",
        title: "AUGI Sable chassis study",
        shortTitle: "AUGI Sable chassis study",
        disposition: "build_in_sandbox",
        actionState: "building_in_sandbox",
        reportCount: 7,
        distinctEmployeeCount: 6,
        departmentsLabel: "BIW Structures, Battery Pack & Vehicle Dynamics",
        assistantSurfaces: [
          "Claude Desktop",
          "Gemini Workspace",
          "ChatGPT Enterprise",
        ],
        problemSummary:
          "Ahead of Gate C2 architecture freeze, BIW, battery, casting, and vehicle dynamics engineers repeatedly run ad-hoc scripts to estimate how switching between SABLE-A (extruded spaceframe), SABLE-B (CTB boron-hybrid), and SABLE-C (front/rear megacastings)—while sweeping wheelbase (2920–3160 mm), track width, and wall gauge (2.2–4.5 mm)—changes structural mass, torsional stiffness (kN·m/deg), and bending sag.",
        policyScope: "Concept geometry parameters · Native CAD excluded",
        matchedAppId: "app_augi_bom_mass_ledger",
        matchedAppName: "Sable Subsystem Mass & CoG Ledger",
        matchedAppVersion: "3.1.0",
        gapExplanation:
          "Sable Subsystem Mass & CoG Ledger v3.1.0 provides static 2D mass tables only and lacks interactive 3D skateboard geometry, parametric dimension sliders, and torsional/bending deflection calculations.",
        recommendedAction:
          "Commission sandbox build of AUGI Sable 3D Chassis Comparator (apps/augi-engineering) using shared organization-ui AUGI theme.",
        reportIds: [
          "rep_augi_20261021_001",
          "rep_augi_20261022_002",
        ],
      },
      {
        clusterId: "wf_augi_02",
        title: "Supplier material certificate lookup",
        shortTitle: "Supplier material certificate lookup",
        disposition: "reuse_existing_app",
        actionState: "pending_triage",
        reportCount: 4,
        distinctEmployeeCount: 2,
        departmentsLabel: "Materials, Megacasting & Sill Extrusion",
        assistantSurfaces: ["Claude Desktop", "Gemini Workspace"],
        problemSummary:
          "Engineers pasted 6082-T6 aluminum-to-22MnB5 boron steel flange thickness pairs into chat to look up self-piercing rivet (SPR) length, material certificate alloy bounds, and galvanic epoxy bead rules.",
        policyScope: "Material cards & joining schedules · Approved scope",
        matchedAppId: "app_augi_fastener_weld_matrix",
        matchedAppName: "BIW Mixed-Material Joining & Adhesive Registry",
        matchedAppVersion: "2.0.3",
        gapExplanation:
          "Matches approved catalog app BIW Mixed-Material Joining & Adhesive Registry v2.0.3. No new permission scope required.",
        recommendedAction:
          "Route directly to existing approved catalog app.",
        reportIds: ["rep_augi_20261023_003"],
      },
      {
        clusterId: "wf_augi_03",
        title: "Rear megacasting flow-length porosity check",
        shortTitle: "Rear megacasting porosity check",
        disposition: "request_missing_evidence",
        actionState: "pending_triage",
        reportCount: 3,
        distinctEmployeeCount: 2,
        departmentsLabel: "Megacasting & BoM Mass Properties",
        assistantSurfaces: ["Gemini Workspace"],
        problemSummary:
          "Casting engineers requested a vacuum-assist runner solidification estimator for the rear shock-tower megacasting, but foundry die-cavity thermal sensor logs are withheld under supplier NDA.",
        policyScope:
          "Restricted foundry_proprietary_die_profiles · Needs sanitized schema",
        gapExplanation:
          "Requires a sanitized synthetic runner geometry schema and supplier NDA clearance before sandbox ingestion.",
        recommendedAction:
          "Request sanitized parametric runner schema from Dr. Astrid Vestergaard while AUGI Sable chassis study proceeds.",
        missingEvidenceDetail:
          "Needs 1 sanitized synthetic runner envelope (.json) cleared by Dr. Astrid Vestergaard and Devendra Rao.",
        missingEvidenceAssignee: "Dr. Astrid Vestergaard (Principal HPDC Specialist)",
        reportIds: ["rep_augi_20261023_004"],
      },
    ],
    reports: [
      {
        reportId: "rep_augi_20261021_001",
        receiptId: "rcpt_augi_4102a",
        submittedAt: "2026-10-21 15:20 UTC",
        employeeId: "emp_augi_01",
        employeeName: "Lukas Lindner",
        employeeTitle: "Lead Chassis & BIW Architect, AUGI Sable",
        departmentLabel: "Body-in-White & Lower Chassis Structures",
        office: "Munich",
        assistantSurface: "Claude Desktop",
        mcpToolName: "heavyrain_submit_work_report",
        userInspectedOutgoingPayload: true,
        hostChatHistoryExcluded: true,
        clusterId: "wf_augi_01",
        dataScope: "sable_parametric_geometry",
        policyScopeLabel: "Concept geometry parameters",
        taskSummary:
          "Compared SABLE-A extruded spaceframe (384 kg, 34.2 kN·m/deg) against SABLE-B CTB boron hybrid (356 kg, 44.6 kN·m/deg) and SABLE-C dual megacasting (338 kg, 49.8 kN·m/deg) across 2920–3160 mm wheelbase sweeps.",
        outgoingPayloadPreview: {
          receipt_id: "rcpt_augi_4102a",
          organization: "AUGI",
          program: "AUGI Sable (Gate C2 Concept Freeze)",
          employee: "Lukas Lindner (Lead Chassis & BIW Architect)",
          approved_scope: "sable_parametric_geometry",
          native_cad_excluded: true,
          chat_transcript_included: false,
          attached_files: [
            "sable_gate_c2_envelopes.json",
            "bredt_batho_scaling_spec.csv",
          ],
        },
        attachments: [
          {
            filename: "sable_gate_c2_envelopes.json",
            kind: "Parametric section envelopes (SABLE-A/B/C)",
            bytes: 4820,
            sha256: "3a19f0c2e8b41720...91d2e4f1",
            policyStatus: "approved",
          },
          {
            filename: "bredt_batho_scaling_spec.csv",
            kind: "Analytical beam/shell stiffness coefficients",
            bytes: 2910,
            sha256: "6b42d8e1a903f512...c8a1b3d9",
            policyStatus: "approved",
          },
        ],
      },
      {
        reportId: "rep_augi_20261022_002",
        receiptId: "rcpt_augi_4118c",
        submittedAt: "2026-10-22 10:42 UTC",
        employeeId: "emp_augi_04",
        employeeName: "Hannah Bergström",
        employeeTitle: "Principal Engineer, Torsional Stiffness & Ride Dynamics",
        departmentLabel: "Vehicle Dynamics, Stiffness & NVH",
        office: "Gothenburg",
        assistantSurface: "Gemini Workspace",
        mcpToolName: "heavyrain_submit_work_report",
        userInspectedOutgoingPayload: true,
        hostChatHistoryExcluded: true,
        clusterId: "wf_augi_01",
        dataScope: "load_case_targets",
        policyScopeLabel: "Concept geometry parameters & load targets",
        taskSummary:
          "Evaluated 18 kN·m axle-twist torsion and 32 kN 3-point vertical bending sag curves across rocker wall gauges (2.2–4.5 mm) with 45x/60x visual deformation scaling.",
        outgoingPayloadPreview: {
          receipt_id: "rcpt_augi_4118c",
          organization: "AUGI",
          employee: "Hannah Bergström (Principal Engineer, Vehicle Dynamics)",
          approved_scope: "load_case_targets",
          chat_transcript_included: false,
          attached_files: ["sable_load_cases_c2.csv"],
        },
        attachments: [
          {
            filename: "sable_load_cases_c2.csv",
            kind: "Static torsion, bending & lateral shear load cases",
            bytes: 3140,
            sha256: "5c71e9b0d421a834...e2f0a1c7",
            policyStatus: "approved",
          },
        ],
      },
      {
        reportId: "rep_augi_20261023_003",
        receiptId: "rcpt_augi_4125d",
        submittedAt: "2026-10-23 13:15 UTC",
        employeeId: "emp_augi_05",
        employeeName: "Kenji Takahashi",
        employeeTitle: "Senior Sill Extrusion & Underbody Load-Path Engineer",
        departmentLabel: "Body-in-White & Lower Chassis Structures",
        office: "Munich",
        assistantSurface: "Claude Desktop",
        mcpToolName: "heavyrain_submit_work_report",
        userInspectedOutgoingPayload: true,
        hostChatHistoryExcluded: true,
        clusterId: "wf_augi_02",
        dataScope: "joining_schedules",
        policyScopeLabel: "Material cards & joining schedules",
        taskSummary:
          "Checked SPR die combinations and structural epoxy bond-line thickness for 3.0 mm AA 6082-T6 sill extrusion to 1.8 mm 22MnB5 boron B-pillar torque ring.",
        outgoingPayloadPreview: {
          receipt_id: "rcpt_augi_4125d",
          organization: "AUGI",
          employee: "Kenji Takahashi (Senior Sill Extrusion Engineer)",
          approved_scope: "joining_schedules",
          chat_transcript_included: false,
          attached_files: ["flange_stackup_query.csv"],
        },
        attachments: [
          {
            filename: "flange_stackup_query.csv",
            kind: "Alloy pair & gauge table (.csv)",
            bytes: 1580,
            sha256: "1f83a2c4b9e07123...a4b5c6d7",
            policyStatus: "approved",
          },
        ],
      },
      {
        reportId: "rep_augi_20261023_004",
        receiptId: "rcpt_augi_4139f",
        submittedAt: "2026-10-23 16:50 UTC",
        employeeId: "emp_augi_02",
        employeeName: "Dr. Astrid Vestergaard",
        employeeTitle: "Principal High-Pressure Die-Casting Specialist",
        departmentLabel: "Materials, Megacasting & Joining Engineering",
        office: "Gothenburg",
        assistantSurface: "Gemini Workspace",
        mcpToolName: "heavyrain_submit_work_report",
        userInspectedOutgoingPayload: true,
        hostChatHistoryExcluded: true,
        clusterId: "wf_augi_03",
        dataScope: "material_cards",
        policyScopeLabel: "Material cards (Foundry die profiles withheld)",
        taskSummary:
          "Estimated flow-length porosity risk across variable 2.6–4.8 mm shock-tower ribs on the SABLE-C AlSi7Mg rear megacasting.",
        outgoingPayloadPreview: {
          receipt_id: "rcpt_augi_4139f",
          organization: "AUGI",
          employee: "Dr. Astrid Vestergaard (Principal HPDC Specialist)",
          approved_scope: "material_cards",
          chat_transcript_included: false,
          attached_files: [],
          withheld_scope_reason:
            "foundry_proprietary_die_profiles requires sanitized synthetic runner schema",
        },
        attachments: [],
        missingEvidenceNote:
          "Raw foundry thermal die logs omitted under supplier NDA; requires sanitized parametric flow-length envelope.",
      },
    ],
    catalogApps: [
      {
        appId: "app_augi_sable_chassis_3d",
        name: "AUGI Sable 3D Chassis Architecture Comparator",
        shortName: "AUGI Sable chassis study",
        slug: "augi-sable-chassis",
        url: AUGI_APP_URL,
        version: "1.0.0-rc1",
        releaseStatus: "in_sandbox_review",
        ownerName: "Lukas Lindner",
        ownerTitle: "Lead Chassis & BIW Architect, AUGI Sable",
        permittedRoles: [
          "Body-in-White & Lower Chassis Structures",
          "Structural Battery Pack Integration",
          "Vehicle Dynamics, Stiffness & NVH",
          "Materials & Megacasting Engineering",
        ],
        dataScopes: [
          "sable_parametric_geometry",
          "material_cards",
          "load_case_targets",
        ],
        summary:
          "Interactive 3D browser workbench for comparing SABLE-A, SABLE-B, and SABLE-C concept skateboard architectures across wheelbase, track, wall gauge, and alloy choices with analytical torsion/bending approximations.",
        capabilities: [
          "Real interactive Three.js / React Three Fiber 3D chassis geometry with orbit, pan, zoom, camera presets, and Reset view.",
          "Side-by-side Baseline vs Candidate configuration comparison with inline concept-approximation scope disclosures beside every readout.",
        ],
        nonCapabilities: [
          "Concept beam-and-shell approximation only; not a finite-element solver, physical rig test, or crashworthiness certification.",
        ],
        notifiedEmployeeCount: 0,
        isHeroCandidate: true,
        feedbackEntries: [
          {
            id: "fb_augi_01",
            employeeName: "Hannah Bergström",
            role: "Principal Engineer, Vehicle Dynamics",
            submittedAt: "2026-10-25 17:45 UTC",
            comment:
              "45x torsion twist amplification clearly shows the shear-web benefit of SABLE-B and SABLE-C over the non-structural SABLE-A tray.",
            routedToWork: false,
          },
        ],
      },
      {
        appId: "app_augi_fastener_weld_matrix",
        name: "BIW Mixed-Material Joining & Adhesive Registry",
        shortName: "Supplier material & joining lookup",
        slug: "biw-joining-registry",
        url: "https://apps.augi.example/biw-joining-registry",
        version: "2.0.3",
        releaseStatus: "catalog_approved",
        ownerName: "Dr. Astrid Vestergaard",
        ownerTitle: "Principal High-Pressure Die-Casting Specialist",
        permittedRoles: [
          "Body-in-White & Lower Chassis Structures",
          "Materials & Megacasting Engineering",
          "Structural Battery Pack Integration",
        ],
        dataScopes: ["material_cards", "joining_schedules"],
        summary:
          "Validates self-piercing rivet (SPR) die combinations, flow-drill screw (FDS) stackups, and structural epoxy bond-line thickness rules for aluminum-to-boron-steel flanges.",
        capabilities: [
          "Checks alloy certificates and galvanic isolation rules for AA 6082-T6 to 22MnB5 boron steel joints.",
        ],
        nonCapabilities: [
          "Does not evaluate full-chassis global torsional or vertical bending stiffness.",
        ],
        notifiedEmployeeCount: 4,
        lastReleasedAt: "2026-09-19 10:30 UTC",
        feedbackEntries: [],
      },
      {
        appId: "app_augi_bom_mass_ledger",
        name: "Sable Subsystem Mass & Center-of-Gravity Ledger",
        shortName: "Sable mass & CoG ledger",
        slug: "sable-mass-ledger",
        url: "https://apps.augi.example/sable-mass-ledger",
        version: "3.1.0",
        releaseStatus: "catalog_approved",
        ownerName: "Claire Dubois",
        ownerTitle: "BIW Mass Properties & BoM Architecture Analyst",
        permittedRoles: [
          "Body-in-White & Lower Chassis Structures",
          "Structural Battery Pack Integration",
          "Vehicle Dynamics, Stiffness & NVH",
          "Materials & Megacasting Engineering",
        ],
        dataScopes: ["bom_mass_targets"],
        summary:
          "Tracks tabular part mass budgets, front/rear axle weight split, and vertical Z-height center-of-gravity across AUGI Sable subsystems.",
        capabilities: [
          "Maintains subsystem mass rollup tables against the 2,490 kg Gate C2 target curb mass.",
        ],
        nonCapabilities: [
          "Static spreadsheet tables only; no 3D chassis geometry or load-case deflection modeling.",
        ],
        notifiedEmployeeCount: 8,
        lastReleasedAt: "2026-08-12 16:00 UTC",
        feedbackEntries: [],
      },
    ],
    initialEvents: [
      {
        eventId: "evt_augi_01",
        timestamp: "18:00:06 UTC",
        actor: "Relay",
        stageLabel: "Weekly cutoff intake",
        tone: "completed",
        headline:
          "Ingested 16 AUGI engineering work reports at Sunday 18:00 UTC cutoff",
        toolName: "relay_ingest_cutoff_batch",
        inputEvidence:
          "16 signed submission receipts (rcpt_augi_4102a .. rcpt_augi_4139f); native CAD solids excluded",
        toolOutput:
          "Verified parametric envelope attachments (.json, .csv) and CAD IP boundary compliance.",
        durationMs: 610,
        decisionSummary:
          "Confirmed only employee-reviewed parametric envelopes and material card IDs were transmitted.",
      },
      {
        eventId: "evt_augi_02",
        timestamp: "18:01:08 UTC",
        actor: "Researcher",
        stageLabel: "Recurrence & catalog match",
        tone: "completed",
        headline:
          "Grouped 16 reports into 3 Gate C2 engineering clusters",
        toolName: "catalog_match_evaluate",
        inputEvidence: "batch_augi_2026_w43 + 3 approved AUGI catalog manifests",
        toolOutput:
          "wf_augi_01 (7 reports): requires 3D parametric comparator; wf_augi_02 (4 reports): matches BIW Joining Registry v2.0.3; wf_augi_03 (3 reports): needs sanitized runner schema.",
        durationMs: 1120,
        decisionSummary:
          "Recommended sandbox build for AUGI Sable chassis study, direct catalog routing for joining lookup, and sanitized schema request for casting porosity.",
      },
      {
        eventId: "evt_augi_03",
        timestamp: "18:01:55 UTC",
        actor: "Connector",
        stageLabel: "Checkpoint 1 · Schema check",
        tone: "completed",
        headline:
          "Checkpoint 1 passed: SABLE-A/B/C parametric envelopes & scaling laws locked",
        toolName: "schema_contract_check",
        inputEvidence:
          "sable_gate_c2_envelopes.json, bredt_batho_scaling_spec.csv, sable_load_cases_c2.csv",
        toolOutput:
          "Verified wheelbase bounds (2920–3160 mm), rocker gauge bounds (2.2–4.5 mm), and explicit non-FEA scope disclosures.",
        durationMs: 1310,
        decisionSummary:
          "Saved Checkpoint 1 snapshot with verified metric/imperial conversion and analytical stiffness formulas.",
        checkpointStep: 1,
      },
      {
        eventId: "evt_augi_04",
        timestamp: "18:02:49 UTC",
        actor: "Cairn",
        stageLabel: "Sandbox orchestration",
        tone: "completed",
        headline:
          "Commissioned isolated sandbox Builder for AUGI Sable chassis study",
        toolName: "cairn_dispatch_sandbox_builder",
        inputEvidence:
          "Authorized by Lukas Lindner (Lead Chassis & BIW Architect) and Devendra Rao (CAE Systems Lead)",
        toolOutput:
          "Assigned Builder targeting apps/augi-engineering (http://127.0.0.1:43171) using @heavy-rain/organization-ui (augi theme).",
        durationMs: 490,
        decisionSummary:
          "Configured interactive Three.js skateboard chassis with orbit/pan/zoom presets and side-by-side configuration comparator.",
      },
      {
        eventId: "evt_augi_05",
        timestamp: "18:03:44 UTC",
        actor: "Builder",
        stageLabel: "Checkpoint 2 · UI assembly",
        tone: "building",
        headline:
          "Assembling AUGI Sable 3D skateboard geometry & parameter dock in sandbox",
        toolName: "sandbox_assemble_ui",
        inputEvidence:
          "Checkpoint 1 parametric contract + AUGI ceramic-ash & cobalt/copper theme tokens",
        toolOutput:
          "Stage 2/3 running: compiling SABLE-A, SABLE-B, and SABLE-C 3D subframes, torsion stress contours, and MetricReadout disclosures.",
        durationMs: 3180,
        decisionSummary:
          "Central preview remains veiled behind smoky blur until Stage 3 verification checks pass.",
        checkpointStep: 2,
      },
    ],
    augiConfigs: [
      {
        configId: "cfg_sable_a_extruded_spaceframe",
        code: "SABLE-A",
        name: "Modular Multi-Extrusion & Stamped Subframe",
        architectureSummary:
          "4-cell AA 6082-T6 sill extrusions, stamped 5754 floor pans, welded multi-piece subframes, and non-structural bolt-in battery pack (15% shear coupling).",
        wheelbaseMm: 3040,
        frontTrackMm: 1690,
        rearTrackMm: 1700,
        wallGaugeMm: 3.2,
        materialLabel: "AA 6082-T6 Extruded & Stamped Aluminum",
        partCount: 142,
        structuralMassKg: 384.0,
        cogZMm: 248.0,
        torsionalStiffnessKnmDeg: 34.2,
        twistAt18KnmDeg: 0.526,
        bendingStiffnessKnMm: 14.8,
        midspanSagMm: 2.16,
        firstModeHz: 36.5,
        strengths: [
          "Lowest prototype tooling investment; standard extrusion dies.",
          "Bolt-out battery pack removal and collision repair serviceability.",
        ],
        weaknesses: [
          "Highest lower-chassis structural mass (384 kg) and part count (142 parts).",
          "Lowest torsional stiffness (34.2 kN·m/deg) due to isolated battery tray.",
        ],
      },
      {
        configId: "cfg_sable_b_cell_to_body_hybrid",
        code: "SABLE-B",
        name: "Cell-to-Body (CTB) Extruded Rocker + 22MnB5 Boron Torque Rings",
        architectureSummary:
          "Structural CTB 800V pack top lid acting as cabin floor shear panel, deep-section 6082-T6 rockers, and hot-stamped 22MnB5 boron steel dash/bulkhead torque rings (82% shear coupling).",
        wheelbaseMm: 3040,
        frontTrackMm: 1690,
        rearTrackMm: 1700,
        wallGaugeMm: 3.0,
        materialLabel: "6082-T6 Sills + 22MnB5 Boron Torque Rings",
        partCount: 86,
        structuralMassKg: 356.0,
        cogZMm: 234.0,
        torsionalStiffnessKnmDeg: 44.6,
        twistAt18KnmDeg: 0.404,
        bendingStiffnessKnMm: 18.9,
        midspanSagMm: 1.69,
        firstModeHz: 42.1,
        strengths: [
          "Saves 28 kg vs SABLE-A while raising torsional stiffness by +30.4% (44.6 kN·m/deg).",
          "Lowers cabin floor Z-height by 14 mm via integrated CTB top cover.",
        ],
        weaknesses: [
          "Requires strict galvanic isolation and controlled SPR/epoxy curing at boron-to-aluminum flanges.",
        ],
      },
      {
        configId: "cfg_sable_c_dual_megacast_ctb",
        code: "SABLE-C",
        name: "Front & Rear HPDC Megacastings + Structural CTB Pack",
        architectureSummary:
          "Single-piece high-pressure vacuum die-cast AlSi7Mg (C611) front and rear megacastings with integral shock towers, bridged by structural CTB 800V pack and 6082-T6 side sills (90% shear coupling).",
        wheelbaseMm: 3080,
        frontTrackMm: 1710,
        rearTrackMm: 1720,
        wallGaugeMm: 3.4,
        materialLabel: "AlSi7Mg (C611) Megacast Ends + 6082-T6 Sills",
        partCount: 31,
        structuralMassKg: 338.0,
        cogZMm: 229.0,
        torsionalStiffnessKnmDeg: 49.8,
        twistAt18KnmDeg: 0.361,
        bendingStiffnessKnMm: 20.4,
        midspanSagMm: 1.57,
        firstModeHz: 45.4,
        strengths: [
          "Lowest structural mass (338 kg, -46 kg vs SABLE-A) and consolidates 142 parts into 31.",
          "Highest concept torsional stiffness (49.8 kN·m/deg) with integral cast shock towers.",
        ],
        weaknesses: [
          "Highest HPDC tooling capital expenditure (9,000-ton class presses) and cast repair complexity.",
        ],
      },
    ],
  },
};

export function createInitialOrgState(
  orgId: OrganizationId,
): OrganizationWorkspaceState {
  const fixture = ORGANIZATION_FIXTURES[orgId];
  return {
    orgId,
    weeklyScheduleActive: true,
    weeklyRunStatus: "Awaiting operator decision",
    // Initial UI starts in the dramatic building state (Stage 2 of 3: UI assembly) matching 03-system-c.png
    buildStage: "stage2",
    lastActiveCheckpoint: 2,
    candidateVersion: "1.0.0-rc1",
    appReleaseStatus: "in_sandbox_review",
    approvedRelease: null,
    changeRequestNotes: [],
    selectedWorker: "Builder",
    selectedReportId: fixture.reports[0]?.reportId ?? "",
    selectedClusterId: fixture.clusters[0]?.clusterId ?? "",
    selectedAppId: fixture.catalogApps[0]?.appId ?? "",
    selectedEventId:
      fixture.initialEvents[fixture.initialEvents.length - 1]?.eventId ?? "",
    clusters: fixture.clusters.map((c) => ({ ...c })),
    catalogApps: fixture.catalogApps.map((a) => ({
      ...a,
      feedbackEntries: [...a.feedbackEntries],
    })),
    events: fixture.initialEvents.map((e) => ({ ...e })),
    isReplaying: false,
    intakeErrorSimulated: false,
    scopeApprovalSimulated: false,
  };
}

// Meridian Crest Capital starts empty. Real work arrives from the HeavyRain app; nothing is scripted.
export const ORGANIZATION_FIXTURES: Record<OrganizationId, OrganizationFixture> = {
  ...DEMO_FIXTURES,
  meridian: {
    ...DEMO_FIXTURES.rookmere,
    orgId: "meridian",
    name: "Meridian Crest Capital",
    shortLabel: "Meridian",
    legalEntity: "Meridian Crest Capital",
    sector: "Private equity",
    headquarters: [],
    totalStaffCount: 0,
    namedRosterCount: 0,
    weeklyBatchId: "batch_meridian_live",
    totalReportsIngested: 0,
    heroTitle: "",
    heroFullName: "",
    heroOrchestratorEyebrow: "",
    heroDecisionSummary: "",
    heroFailedHeadline: "",
    heroFailedDetail: "",
    externalAppUrl: undefined,
    clusters: [],
    reports: [],
    catalogApps: [],
    initialEvents: [],
    rookmereCompanies: [],
    augiConfigs: [],
    augiInlineDisclosure: undefined,
  },
};
